import { watch } from 'vue'
import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

export const LOGIN_ROUTE = 'login'
const LOGIN_PATH = '/login'
/** The home of a logged-in visitor: the dashboard. The root path leads there too. */
const HOME_PATH = '/dashboard'

/**
 * Where to go after logging in: the page the visitor was headed for (the `redirect` query of the login
 * page) or the home page. Only a path inside this app is honoured. Anything else, such as another site,
 * a `//host` shortcut or a backslash that browsers read as a slash, would turn the login page into an
 * open redirect, so it falls back to the home page. The login page itself is no destination either.
 */
export function safeRedirect(target: unknown): string {
  if (typeof target !== 'string') return HOME_PATH
  if (!target.startsWith('/') || target.startsWith('//')) return HOME_PATH
  if (target.includes('\\') || /[\u0000-\u001f\u007f]/.test(target)) return HOME_PATH
  if (target.split(/[?#]/)[0] === LOGIN_PATH) return HOME_PATH
  return target
}

/**
 * Guards the routes by their `meta`: `requiresAuth` sends a visitor who is not logged in to the login
 * page, remembering where they were going, and `guestOnly` keeps a logged-in visitor off pages such as
 * the login page. A session that ends while a protected page is open, by logout, expiry or a logout in
 * another tab, also leaves that page, and so does one that ends while the page is still loading.
 *
 * Call it after Pinia is installed, and before the router starts.
 */
export function installAuthGuard(router: Router): void {
  const auth = useAuthStore()

  router.beforeEach((to) => {
    const signedIn = auth.checkSession()
    if (to.meta.requiresAuth && !signedIn) {
      return { name: LOGIN_ROUTE, query: { redirect: to.fullPath } }
    }
    if (to.meta.guestOnly && signedIn) return safeRedirect(to.query.redirect)
  })

  // A page loads on demand, and in the meantime the API can refuse the token. The check above was
  // made before that, and the watcher below only sees the route that is on screen, not the one that
  // is about to be, so the session is checked once more just before the navigation is confirmed.
  router.beforeResolve((to) => {
    if (to.meta.requiresAuth && !auth.checkSession()) {
      return { name: LOGIN_ROUTE, query: { redirect: to.fullPath } }
    }
  })

  watch(
    () => auth.isAuthenticated,
    (signedIn) => {
      const current = router.currentRoute.value
      if (!signedIn && current.meta.requiresAuth) {
        void router.replace({ name: LOGIN_ROUTE, query: { redirect: current.fullPath } })
      }
    },
  )
}
